import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type EngagementLevel = "poor" | "average" | "good";

function roundNumberToDecimals(number: number, decimals: number): string {
    return number.toFixed(decimals).replace(/\.?0+$/, "");
}

export function scoreTagTooltipText(ratingsCount: number) {
    if (!ratingsCount) {
        return "No reviews";
    }

    return `Based on ${ratingsCount} ${ratingsCount === 1 ? "review" : "reviews"}`;
}

function ScoreTag({ average }: { average: number }) {
    const engagement: { level: EngagementLevel; label: string } = (() => {
        const label = `${roundNumberToDecimals(average, 1)}/5`;

        if (average && average >= 4) {
            return { level: "good", label };
        }

        if (average && average >= 3) {
            return { level: "average", label };
        }

        return { level: "poor", label };
    })();

    switch (engagement.level) {
        case "good":
            return (
                <Badge variant="green" size="sm">
                    {engagement.label}
                </Badge>
            );
        case "average":
            return (
                <Badge variant="amber" size="sm">
                    {engagement.label}
                </Badge>
            );
        case "poor":
            return (
                <Badge variant="red" size="sm">
                    {engagement.label}
                </Badge>
            );
        default: {
            const _exhaustive: never = engagement.level;
            return _exhaustive;
        }
    }
}

export function ScoreIndicator({
    average,
    ratingsCount,
}: {
    average: number;
    ratingsCount: number;
}) {
    return (
        <Tooltip>
            <TooltipTrigger render={<span className="inline-flex" />}>
                <ScoreTag key={average} average={average} />
            </TooltipTrigger>
            <TooltipContent side="top" align="start">
                {scoreTagTooltipText(ratingsCount)}
            </TooltipContent>
        </Tooltip>
    );
}
